<template>
  <hel-module title="年累签约情况" :class="scope.clsPrefix">
    <template slot="extra">
      <span v-if="hasGrowthRate" :class="['trend-badge', growthRate >= 0 ? 'trend-up' : 'trend-down']">
        {{ growthRate >= 0 ? "+" : "" }}{{ growthRate.toFixed(1) }}%
      </span>
    </template>
    <div class="revenue-container">
      <div id="revenueChart" class="revenue-chart"></div>
      <div class="revenue-legend">
        <div class="legend-item">
          <span class="legend-line" style="background: #4a5ff3"></span>
          <span class="legend-text">总体签约（万元）</span>
        </div>
        <div class="legend-item">
          <span class="legend-line" style="background: #9ca3af"></span>
          <span class="legend-text">外单签约（万元）</span>
        </div>
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from './common/module/index.vue'
import * as echarts from 'echarts'

import axios from 'axios'
export default {
  name: 'RevenueAnalysis',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-revenue-analysis'
      },
      summary: {},
      rows: [],
      growthRate: 0,
      hasGrowthRate: false,
      chartInstance: null,
      resizeObserver: null,
      lastWidth: 0,
      lastHeight: 0,
      resizeTimeout: null
    }
  },
  computed: {},
  created() {
    this.fetchRevenueData()
  },
  mounted() {
    this.$nextTick(() => {
      this.setupResizeObserver()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
  },
  methods: {
    async fetchRevenueData() {
      try {
        const year = new Date().getFullYear().toString()
        const currentMonth = new Date().getMonth() + 1
        const tenantId = this.$store.state.user?.currentTenant?.code || ''
        // const tenantId = '39CCF998'

        const response = await axios.post(
          'https://bigdata.cosmoplat.com/prod-api/system/api/call/chaingroup/sign',
          {
            secret: '88py6X',
            paramList: [
              { paramName: 'tenant_id', paramValue: tenantId },
              { paramName: 'year', paramValue: year }
            ]
          },
          { headers: { 'Content-Type': 'application/json' }}
        )

        const { data } = response.data

        if (data && data.length > 0) {
          const filteredData = data.filter((item) => parseInt(item.period) <= currentMonth)

          // 取当前月"合计"类型的 yoy 作为年累增长率
          const currentMonthItem = filteredData.find(
            (item) => parseInt(item.period) === currentMonth && item.order_type === '合计'
          )
          if (currentMonthItem && currentMonthItem.yoy != null) {
            this.growthRate = Number(currentMonthItem.yoy)
            this.hasGrowthRate = true
          } else {
            this.hasGrowthRate = false
          }

          const monthMap = {}
          filteredData.forEach((item) => {
            const mKey = parseInt(item.period)
            if (!monthMap[mKey]) monthMap[mKey] = {}

            if (item.order_type === '合计') {
              monthMap[mKey].总体 = item.actual
            } else if (item.order_type === '外单') {
              monthMap[mKey].外单 = item.actual
            }
          })

          this.rows = Object.keys(monthMap)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((m) => ({
              月份: m + '月',
              总体: monthMap[m].总体 || 0,
              外单: monthMap[m].外单 || 0
            }))

          this.$nextTick(() => {
            this.drawChart()
          })
        }
      } catch (error) {
        console.error('获取签约数据失败:', error)
      }
    },

    formatNumber(value) {
      return Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    drawChart() {
      const chartEl = document.getElementById('revenueChart')
      const chart = echarts.init(chartEl)
      const months = this.rows.map((r) => r.月份)
      const totalSignData = this.rows.map((r) => r.总体)
      const externalSignData = this.rows.map((r) => r.外单)

      const option = {
        grid: {
          top: 20,
          right: 5,
          bottom: 20,
          left: 40
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e8ecf0',
          borderWidth: 1,
          textStyle: {
            color: '#333'
          },
          formatter: (params) => {
            let html = `<div style="font-weight:600;margin-bottom:8px;">${params[0].name}</div>`
            params.forEach((param) => {
              html += `<div style="display:flex;align-items:center;justify-content:space-between;gap:20px;margin:4px 0;">
                <span style="display:flex;align-items:center;gap:6px;">
                  <span style="width:8px;height:8px;border-radius:50%;background:${
  param.color
};"></span>
                  ${param.seriesName}
                </span>
                <span style="font-weight:600;">${Number(
    param.value
  ).toLocaleString('zh-CN', { maximumFractionDigits: 0 })} 万元</span>
              </div>`
            })
            return html
          }
        },
        xAxis: {
          type: 'category',
          data: months,
          axisLine: {
            lineStyle: {
              color: '#e8ecf0'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#64748b',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            formatter: (value) => {
              if (value >= 10000) {
                return (value / 10000).toFixed(0) + '万'
              }
              return value
            }
          },
          splitLine: {
            lineStyle: {
              color: '#f1f5f9',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: '总体签约',
            type: 'line',
            data: totalSignData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 2,
              color: '#4a5ff3'
            },
            itemStyle: {
              color: '#4a5ff3',
              borderWidth: 2,
              borderColor: '#fff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(74, 95, 243, 0.2)' },
                { offset: 1, color: 'rgba(74, 95, 243, 0.02)' }
              ])
            }
          },
          {
            name: '外单签约',
            type: 'line',
            data: externalSignData,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 2,
              color: '#9ca3af'
            },
            itemStyle: {
              color: '#9ca3af',
              borderWidth: 2,
              borderColor: '#fff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(156, 163, 175, 0.15)' },
                { offset: 1, color: 'rgba(156, 163, 175, 0.02)' }
              ])
            }
          }
        ]
      }
      chart.setOption(option)
      this.chartInstance = chart
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },
    setupResizeObserver() {
      const chartEl = document.getElementById('revenueChart')
      if (chartEl) {
        this.resizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const { width, height } = entry.contentRect
            if (width > 0 && height > 0 && this.chartInstance) {
              if (
                Math.abs(width - this.lastWidth) > 1 ||
                Math.abs(height - this.lastHeight) > 1
              ) {
                this.lastWidth = width
                this.lastHeight = height
                if (this.resizeTimeout) {
                  clearTimeout(this.resizeTimeout)
                }
                this.resizeTimeout = setTimeout(() => {
                  if (this.chartInstance) {
                    this.chartInstance.resize()
                  }
                }, 100)
              }
            }
          })
        })
        this.resizeObserver.observe(chartEl)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-revenue-analysis";
.#{$class-prefix} {
  .trend-badge {
    font-size: 12px;
    font-weight: 500;
    padding: 2px 8px;
    height: 16px;
    border-radius: 8px;
    font-family: PingFangSC-Regular;
    font-weight: 400;
    font-size: 12px;
    line-height: 12px;
    &.trend-up {
      background: #f6ffed;
      color: #73b950;
    }
    &.trend-down {
      background: #feeded;
      color: #f44f4f;
    }
  }
  .revenue-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 8px;
  }
  .revenue-chart {
    width: 100%;
    flex: 1;
    min-height: 150px;
  }
  .revenue-legend {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f1f5f9;
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .legend-line {
    width: 12px;
    height: 2px;
    border-radius: 1px;
  }
  .legend-text {
    font-size: 11px;
    color: #64748b;
  }
}
</style>
